import { formatCurrency } from './invoiceData';

export const expenseAccounts = [
  { value: 'supplies', label: 'Cleaning Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'vehicle', label: 'Vehicle / Gas' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'rent', label: 'Rent / Lease' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'marketing', label: 'Marketing / Advertising' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'software', label: 'Software / Subscriptions' },
  { value: 'office', label: 'Office Supplies' },
  { value: 'maintenance', label: 'Maintenance / Repairs' },
  { value: 'other', label: 'Other' }
];

export const depositCategories = [
  { value: 'cleaning_service', label: 'Cleaning Service' },
  { value: 'deep_clean', label: 'Deep Clean' },
  { value: 'move_in_out', label: 'Move In/Out' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'deposit', label: 'Client Deposit' },
  { value: 'tip', label: 'Tip' },
  { value: 'refund', label: 'Refund (negative)' },
  { value: 'other_income', label: 'Other Income' }
];

export const getAccountLabel = (value) => {
  return expenseAccounts.find(a => a.value === value)?.label || value;
};

// Default window for calling two charges "the same" — 5 days catches same
// charge posted with slightly different dates between statement/app.
export const DEFAULT_DUPLICATE_WINDOW_DAYS = 5;

// Returns a Set of ids for items that look like duplicate charges.
// Two items are flagged as a pair when:
//   1. their amount matches (compared in whole cents), AND
//   2. the bucket key from `getBucketKey` matches (e.g. accountId for
//      expenses/deposits, or "from|to" for transfers), AND
//   3. their dates fall within `windowDays` of each other.
// Every item in a matched pair (or larger chain) ends up in the returned set.
export function findDuplicateIdSet(items, {
  getAmount,
  getBucketKey,
  getDate,
  windowDays = DEFAULT_DUPLICATE_WINDOW_DAYS
}) {
  const buckets = new Map();
  for (const item of items || []) {
    const cents = Math.round((Number(getAmount(item)) || 0) * 100);
    if (!cents) continue;
    const bucketKey = getBucketKey(item);
    // Skip items we can't meaningfully compare (no account assignment).
    if (bucketKey == null || bucketKey === '') continue;
    const key = `${cents}|${bucketKey}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  }

  const duplicateIds = new Set();
  const msWindow = windowDays * 24 * 60 * 60 * 1000;

  for (const group of buckets.values()) {
    if (group.length < 2) continue;
    const withDates = group
      .map(it => {
        const raw = getDate(it);
        const t = raw ? new Date(raw).getTime() : NaN;
        return { it, t };
      })
      .filter(x => !Number.isNaN(x.t))
      .sort((a, b) => a.t - b.t);

    for (let i = 0; i < withDates.length; i++) {
      for (let j = i + 1; j < withDates.length; j++) {
        if (withDates[j].t - withDates[i].t > msWindow) break;
        duplicateIds.add(withDates[i].it.id);
        duplicateIds.add(withDates[j].it.id);
      }
    }
  }
  return duplicateIds;
}

export const getDepositCategoryLabel = (value) => {
  return depositCategories.find(c => c.value === value)?.label || value;
};

// Finds records that are EXACT duplicates — same description, notes, amount,
// and date. For every group of matching records, all but one (the oldest by
// `createdAt`, falling back to id) are returned as a list of ids to delete.
// Callers pass getters so the same function works for expenses (dueDate) and
// deposits/transfers (date).
export function findExactDuplicateIdsToDelete(items, {
  getDescription = it => it.description,
  getNotes = it => it.notes,
  getAmount = it => it.amount,
  getDate
}) {
  const groups = new Map();
  for (const item of items || []) {
    if (!item || !item.id) continue;
    const description = (getDescription(item) || '').trim();
    const notes = (getNotes(item) || '').trim();
    const cents = Math.round((Number(getAmount(item)) || 0) * 100);
    const date = (getDate(item) || '').toString().trim();
    const key = `${description}|${notes}|${cents}|${date}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }

  const idsToDelete = [];
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => {
      const at = a.createdAt ? new Date(a.createdAt).getTime() : Number.POSITIVE_INFINITY;
      const bt = b.createdAt ? new Date(b.createdAt).getTime() : Number.POSITIVE_INFINITY;
      if (at !== bt) return at - bt;
      return String(a.id).localeCompare(String(b.id));
    });
    for (let i = 1; i < sorted.length; i++) {
      idsToDelete.push(sorted[i].id);
    }
  }
  return idsToDelete;
}

export { formatCurrency };
